import React from "react";
import { useTranslation } from "react-i18next";
import { Radio } from "@mui/material";
import ListView from "@common/components/ListDetailViews/ListView";
import { AccountsVisibility, RolePermissions } from "@services/user/types";
import useRole from "../hooks/useRole";

interface ComponentAccessProps {
  canEdit?: boolean;
}

type RowType = {
  key: keyof RolePermissions;
  label: string;
  hidden: boolean;
  viewOnly: boolean;
  fullAccess: boolean;
};

const components: {
  label: string;
  key: keyof RolePermissions;
}[] = [
  {
    label: "Roles & Capabilities",
    key: "rolesCapabilities",
  },
  {
    label: "Global Configuration",
    key: "globalConfiguration",
  },
  {
    label: "Accounts",
    key: "accounts",
  },
  {
    label: "Account Configuration",
    key: "accountConfiguration",
  },
  {
    label: "Users",
    key: "users",
  },
  {
    label: "Locations",
    key: "locations",
  },
  {
    label: "Rooms",
    key: "rooms",
  },
  {
    label: "Devices",
    key: "devices",
  },
  {
    label: "Meetings",
    key: "meetings",
  },
  {
    label: "Meetings E-Logs & Revisions",
    key: "meetingsElogsAndRevisions",
  },
  {
    label: "Tickets",
    key: "tickets",
  },
  {
    label: "Reports",
    key: "reports",
  },
  {
    label: "Analytics",
    key: "analyticsReports",
  },
  {
    label: "Attachments",
    key: "attachments",
  },
];

const ComponentAccess = (props: ComponentAccessProps): React.JSX.Element => {
  const { canEdit } = props;
  const { t } = useTranslation();
  const { role, setRole } = useRole();

  const onChange = (key: keyof RolePermissions, value: string) => {
    setRole((old) => {
      const rolePermissions = old.rolePermissions ?? { admin: true };
      return {
        ...old,
        rolePermissions: { ...rolePermissions, [key]: value },
      };
    });
  };

  const disabled = !canEdit;
  const columns: ColDef<RowType>[] = [
    {
      key: "label",
      label: "Component",
      sortable: false,
    },
    {
      key: "hidden",
      label: t("Hidden"),
      sortable: false,
      render: (c) => (
        <Radio
          disabled={disabled}
          onClick={() => onChange(c.key, "NoAccess")}
          checked={c.hidden}
        />
      ),
    },
    {
      key: "viewOnly",
      label: t("View Only"),
      sortable: false,
      render: (c) => (
        <Radio
          disabled={disabled}
          onClick={() => onChange(c.key, "ViewOnly")}
          checked={c.viewOnly}
        />
      ),
    },
    {
      key: "fullAccess",
      label: t("Full Access"),
      sortable: false,
      render: (c) => (
        <Radio
          disabled={disabled}
          onClick={() => onChange(c.key, "FullAccess")}
          checked={c.fullAccess}
        />
      ),
    },
  ];

  const allAccountsPermissions = ["globalConfiguration", "rolesCapabilities"];
  const data = components
    .filter((c) => {
      return (
        !allAccountsPermissions.includes(c.key) ||
        (allAccountsPermissions.includes(c.key) &&
          role?.rolePermissions?.adminAccountsVisibility ===
            AccountsVisibility.AllAccounts)
      );
    })
    .map<RowType>((c) => ({
      key: c.key,
      label: c.label,
      hidden: role?.rolePermissions?.[c.key] === "NoAccess",
      viewOnly: role?.rolePermissions?.[c.key] === "ViewOnly",
      fullAccess: role?.rolePermissions?.[c.key] === "FullAccess",
    }));

  return (
    <ListView
      columns={columns}
      data={data}
      disableColumnResize
      sx={{
        height: "fit-content",
        "& > div": {
          overflow: "hidden",
        },
      }}
    />
  );
};

export default ComponentAccess;
